/**
 * FormCheckSuggestionModal — the form check's note and suggested rewrite, opened
 * from the small pill under a participant's own text (InputFeedbackIndicator
 * variant="pill"). Portaled to <body>: the pill sits inside another modal, and
 * a modal rendered inside a modal's stacking context is clipped by it.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { AlertTriangle, Sparkles } from 'lucide-react';
import ModalWrapper, { ModalCard } from './ModalWrapper';
import { useTranslation } from '../hooks/useTranslation.jsx';
import { FORM_CHECK_COLOR } from '../utils/formCheckDisplay';

const ACCEPT_COLOR = '#22c55e';

const Title = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${(p) => p.$color};
`;

const Issue = styled.p`
  margin: 0 0 10px;
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.88);

  &:last-of-type { margin-bottom: 0; }
`;

const SuggestionLabel = styled.div`
  margin: 18px 0 8px;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
`;

const SuggestionText = styled.div`
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.08);
  font-size: 0.95rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.95);
  white-space: pre-wrap;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
`;

const Btn = styled.button`
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
`;

const AcceptBtn = styled(Btn)`
  background: ${ACCEPT_COLOR};
  color: #0f172a;
  border-color: ${ACCEPT_COLOR};

  &:hover { filter: brightness(1.05); }
`;

const QuietBtn = styled(Btn)`
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.85);
  border-color: rgba(255, 255, 255, 0.15);

  &:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
`;

export default function FormCheckSuggestionModal({
  open,
  severity = 'advisory',
  issues = [],
  suggestedText = null,
  onAcceptSuggestion,
  onDismiss,
  onClose,
}) {
  const { t } = useTranslation();
  if (!open || typeof document === 'undefined') return null;
  const Icon = severity === 'blocking' ? AlertTriangle : Sparkles;

  return createPortal(
    <ModalWrapper visible onClose={onClose} zIndex={1200} closeOnBackdrop>
      {/* ModalCard keeps only size/className/style, so the dialog role sits on a contents-only wrapper */}
      <div role="dialog" aria-modal="true" aria-label={t(`formCheck.pill.${severity}`)} style={{ display: 'contents' }}>
      <ModalCard size="md">
        <ModalCard.Header>
          <Title $color={FORM_CHECK_COLOR}>
            <Icon size={20} />
            {t(`formCheck.pill.${severity}`)}
          </Title>
        </ModalCard.Header>
        <ModalCard.Body>
          {issues.map((issue, index) => (
            <Issue key={issue.code || index}>{issue.message}</Issue>
          ))}
          {suggestedText && (
            <>
              <SuggestionLabel>{t('formCheck.suggestedLabel')}</SuggestionLabel>
              <SuggestionText>{suggestedText}</SuggestionText>
            </>
          )}
          <Actions>
            {suggestedText && onAcceptSuggestion && (
              <AcceptBtn type="button" onClick={() => { onAcceptSuggestion(suggestedText); onClose?.(); }}>
                {t('formCheck.acceptSuggestion')}
              </AcceptBtn>
            )}
            {onDismiss && (
              <QuietBtn type="button" onClick={() => { onDismiss(); onClose?.(); }}>
                {t('formCheck.dismiss')}
              </QuietBtn>
            )}
          </Actions>
        </ModalCard.Body>
      </ModalCard>
      </div>
    </ModalWrapper>,
    document.body,
  );
}
